<script lang="ts" setup>
	import { supportLines } from '~/assets/json/customer-support'

	export type EligibilityType =
		'new-user' | 'expired-plan' | 'churned-plan' | 'interval-change' | 'tier-change' | 'support'

	export type EligibilityResponse = {
		is_expired?: boolean
		new_user?: boolean
		churned_user?: boolean
		stay_interval?: number
		can_change_plan?: boolean
		no_of_month?: number | string
		is_two_tier_above?: boolean
		is_two_tier_below?: boolean
		current_plan?: string
		lowest_plan?: string
		highest_plan?: string
		lowest_plan_id?: string
		highest_plan_id?: string
	}

	const { planId, from, to } = defineProps<{
		planId: string
		response?: EligibilityResponse
		from?: string
		to?: string
	}>()
	const isOn = defineModel<boolean>({ default: false })
	const type = defineModel<EligibilityType>('type', { default: 'support' })
	const grades = shallowRef('')
	const emit = defineEmits<{
		(event: 'close'): void
		(event: 'adjust-plan', arg: string): void
	}>()
	const migrationInfo = shallowRef<EligibilityResponse | null>(null)
	const typeText = computed<Record<EligibilityType, { header: string; paragraph?: string }>>(() => ({
		'new-user': {
			header: 'Hey Genius, just a bit more time',
			paragraph:
				"Before switching plans, you need to complete at least <span class='font-semibold text-primary-2'>1 full month</span> on your current plan. <br /> <br /> You're almost there! <br /> Once you renew after your first month, you can go ahead and switch plans to enjoy premium access to care and its full benefits. <br /> <br /> Come back after your first renewal to make the switch"
		},
		'churned-plan': {
			header: 'Hey Genius, not yet time',
			paragraph: `We're glad to have you back. <br /> <br /> Since your plan was recently reactivated, you'll need to consistently stay on it for at least 3 months before making any changes. <br /> <br /> We're committed to giving you great service during this time. <br /> If you need more clarity, our <a href='${supportLines[2].link}' target='_blank' class='font-semibold text-primary-500'>support team</a> is just a message away`
		},
		'expired-plan': {
			header: 'Plan Expired',
			paragraph:
				"Hi there! You can only change a plan that's currently active. Once you renew your expired plan, you'll be able to switch to a new one. <br /> <br /> Please proceed to renew your current plan to continue."
		},
		'interval-change': {
			header: 'Oh no, not eligible just yet',
			paragraph: `After your last plan change, you're required to stay on your new plan for <span class='font-semibold text-primary-2'> ${migrationInfo.value?.no_of_month ?? 0} month(s)</span> of consistent renewal before switching again. <br /> We understand you may need a change now, but it's just a little longer. <br /><br /> Need help? <br /> Our support team is ready to assist — you're highly valued here.`
		},
		'tier-change': {
			header: "Let's keep it gradual",
			paragraph: `You're eligible to ${migrationInfo.value?.is_two_tier_above ? 'upgrade' : 'downgrade'}, but only <span class='font-semibold text-primary-2'>${migrationInfo.value?.is_two_tier_above ? '2 tiers above' : migrationInfo.value?.is_two_tier_below ? '2 tiers below' : 'N/A'}</span> your current plan. You're currently on <span class='font-semibold text-purple-500'>${migrationInfo.value?.current_plan ?? 'N/A'}</span>, so the lowest/highest plan you can move to is <span class='font-semibold text-primary-2'>${migrationInfo.value?.lowest_plan ?? migrationInfo.value?.highest_plan ?? 'N/A'}</span>.`
		},
		support: {
			header: 'Plan Migration',
			paragraph: `To assist you efficiently, <br /> please email <a href="mailto:hello@mycovergenius.com" class="text-primary-500" target="_blank" rel="noopener noreferrer">hello@mycovergenius.com</a> with: <br /> 
        <ul class="list-decimal text-sm text-gray-500 font-medium list-inside">
            <li class="mb-2">The plan you wish to ${grades.value} to</li>
            <li>Your reason for requesting the change</li>
        </ul>
        <br />
        Our support team will respond promptly to guide you through the next steps.`
		}
	}))
	const dialogTemplate = useTemplateRef('dialog')

	const openEmailClient = () => {
		const email = 'hello@mycovergenius.com'
		const subject = encodeURIComponent(`Plan ${grades.value}`)
		const body = encodeURIComponent(
			`Hello, I want to ${grades.value} my business plan from ${migrationInfo.value?.current_plan ?? from ?? ''} to ${migrationInfo.value?.lowest_plan ?? to ?? ''}.The current plan id is (${planId}).  My reason for this request is: {provide your reason here}`
		)
		const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`

		window.open(gmailLink, '_blank', 'noopener noreferrer')
	}
	const { openInterCom } = useIntercom()
	const closeModal = async () => {
		isOn.value = false
		if (type.value === 'support') {
			return openEmailClient()
		}
		if (type.value === 'interval-change') {
			openInterCom()
			return null
		}
		if (type.value === 'expired-plan') {
			return await navigateTo({
				name: 'mycover-plan_id-renew',
				params: {
					plan_id: planId
				}
			})
		}
		emit('close')
	}
	const checkEligibility = (arg: EligibilityResponse, gradeStatus: string) => {
		if (arg) {
			if (arg.is_expired) {
				type.value = 'expired-plan'
			} else if (arg.new_user) {
				type.value = 'new-user'
			} else if (arg.churned_user) {
				type.value = 'churned-plan'
			} else if (arg.can_change_plan === false && !(arg.is_two_tier_above || arg.is_two_tier_below)) {
				type.value = 'interval-change'
			} else if (arg.is_two_tier_above || arg.is_two_tier_below) {
				type.value = 'tier-change'
			} else if (!Object.values(arg).length) {
				type.value = 'support'
			}
			grades.value = typeof gradeStatus === 'string' ? gradeStatus : ''
			migrationInfo.value = arg
			isOn.value = true
		}
	}
	const adjustPlan = () => {
		emit('adjust-plan', migrationInfo.value?.lowest_plan_id ?? migrationInfo.value?.highest_plan_id ?? 'N/A')
		emit('close')
	}
	defineExpose({
		dialogTemplate,
		checkEligibility
	})
</script>

<template>
	<div ref="dialog">
		<dialogs-error
			v-model:show="isOn"
			type="warning"
			:header="typeText?.[type]?.header ?? ''"
			:paragraph="typeText?.[type]?.paragraph ?? ''"
			wrapper-class="bg-gray-50 py-8 px-5 rounded-sm space-y-8"
			:icon-props="{ wrapperClass: type !== 'interval-change' && type !== 'support' && 'bg-error-100' }"
			@close="closeModal"
		>
			<template #icon>
				<tools-icon
					:dir="type === 'interval-change' ? 'fill' : type === 'support' ? 'communication' : 'system'"
					:icon="type === 'interval-change' ? 'forbidden' : type === 'support' ? 'mail' : 'timer'"
					:path-class="
						type === 'interval-change'
							? 'fill-red-500'
							: type === 'support'
								? 'stroke-primary-500'
								: 'fill-error-500'
					"
					:height="40"
					:width="40"
				/>
			</template>
			<template #default><slot /></template>
			<template #button>
				<div class="flex w-full flex-wrap items-center justify-between gap-4 sm:my-10 sm:gap-10">
					<slot name="button" :close="closeModal">
						<button
							v-if="type != 'tier-change'"
							type="button"
							name="noted"
							class="default-btn flex-1 justify-center bg-primary-2"
							@click="closeModal"
						>
							<span v-show="type === 'expired-plan'">Renew Plan</span>
							<span v-show="type === 'interval-change'">Contact Support</span>
							<span v-show="type === 'support'">Open Mail</span>

							<span v-show="!['expired-plan', 'interval-change', 'support'].includes(type)">Noted</span>
						</button>
						<template v-else>
							<button
								type="button"
								class="default-btn flex-1 justify-center bg-gray-200"
								@click="closeModal"
							>
								Back
							</button>
							<button
								type="button"
								class="default-btn flex-1 justify-center bg-primary-2"
								@click="adjustPlan"
							>
								Adjust to {{ migrationInfo?.lowest_plan ?? migrationInfo?.highest_plan ?? 'N/A' }}
							</button>
						</template>
					</slot>
				</div>
			</template>
		</dialogs-error>
	</div>
</template>
